import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { Shield, Heart, Activity, ArrowRight, X } from 'lucide-react';

type IncidentType = 'fire' | 'medical' | 'rescue' | 'mva' | 'hazmat' | 'other';
type SeverityLevel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

interface IncidentFormData {
  incidentType: IncidentType | '';
  severity: SeverityLevel | null;
  currentState: string;
  physicalSymptoms: string[];
  emotionalState: string[];
  whatHappened: string;
  immediateNeeds: string;
}

const incidentTypes = [
  { value: 'fire', label: 'Fire', icon: '🔥' },
  { value: 'medical', label: 'Medical Emergency', icon: '🚑' },
  { value: 'rescue', label: 'Rescue', icon: '🛟' },
  { value: 'mva', label: 'Motor Vehicle Accident', icon: '🚗' },
  { value: 'hazmat', label: 'Hazmat', icon: '☣️' },
  { value: 'other', label: 'Other', icon: '⚠️' },
];

const physicalSymptoms = [
  'Racing heart',
  'Trembling/shaking',
  'Sweating',
  'Nausea',
  'Headache',
  'Tight chest',
  'Fatigue',
  'Dizziness',
];

const emotionalStates = [
  'Shocked',
  'Anxious',
  'Angry',
  'Sad',
  'Numb',
  'Guilty',
  'Relieved',
  'Overwhelmed',
];

export const PostIncidentCheckin: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<IncidentFormData>({
    incidentType: '',
    severity: null,
    currentState: '',
    physicalSymptoms: [],
    emotionalState: [],
    whatHappened: '',
    immediateNeeds: '',
  });

  const handleNext = () => {
    if (step === 1 && (!formData.incidentType || !formData.severity)) return;
    if (step === 2 && !formData.currentState.trim()) return;
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Save incident to Firestore
      const incidentData = {
        userId: user.uid,
        incidentType: formData.incidentType,
        severity: formData.severity,
        currentState: formData.currentState,
        physicalSymptoms: formData.physicalSymptoms,
        emotionalState: formData.emotionalState,
        whatHappened: formData.whatHappened,
        immediateNeeds: formData.immediateNeeds,
        createdAt: serverTimestamp(),
        timestamp: new Date().toISOString(),
      };

      const incidentRef = await addDoc(
        collection(db, 'users', user.uid, 'incidents'),
        incidentData
      );

      // Build context message for GRIPS chatbot
      const gripsContext = `I just experienced a critical incident. Here's what happened:

**Incident Type:** ${incidentTypes.find(t => t.value === formData.incidentType)?.label || 'Other'}
**Severity Level:** ${formData.severity}/10
**How I'm feeling:** ${formData.currentState}
**Physical symptoms:** ${formData.physicalSymptoms.join(', ') || 'None'}
**Emotional state:** ${formData.emotionalState.join(', ') || 'Not specified'}

${formData.whatHappened ? `**What happened:** ${formData.whatHappened}` : ''}
${formData.immediateNeeds ? `**What I need:** ${formData.immediateNeeds}` : ''}

I'd like to talk about this and get support.`;

      // Navigate to GRIPS with pre-filled message
      navigate('/grips', {
        state: {
          initialMessage: gripsContext,
          incidentId: incidentRef.id,
        },
      });
    } catch (error) {
      console.error('Error saving incident:', error);
      alert('There was an error saving your incident. Please try again.');
      setLoading(false);
    }
  };

  const toggleSymptom = (symptom: string) => {
    setFormData(prev => ({
      ...prev,
      physicalSymptoms: prev.physicalSymptoms.includes(symptom)
        ? prev.physicalSymptoms.filter(s => s !== symptom)
        : [...prev.physicalSymptoms, symptom],
    }));
  };

  const toggleEmotionalState = (state: string) => {
    setFormData(prev => ({
      ...prev,
      emotionalState: prev.emotionalState.includes(state)
        ? prev.emotionalState.filter(s => s !== state)
        : [...prev.emotionalState, state],
    }));
  };

  const getSeverityColor = (level: number) => {
    if (level <= 3) return 'bg-green-500';
    if (level <= 6) return 'bg-yellow-500';
    if (level <= 8) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getSeverityLabel = (level: number) => {
    if (level <= 3) return 'Low';
    if (level <= 6) return 'Moderate';
    if (level <= 8) return 'High';
    return 'Critical';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <X className="w-5 h-5 mr-2" />
            <span>Back to Dashboard</span>
          </button>
          
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-red-100 p-3 rounded-full">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Post-Incident Check-in
            </h1>
          </div>
          <p className="text-gray-600">
            Let's take a moment to assess how you're doing after this incident.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Step {step} of 3
            </span>
            <span className="text-sm text-gray-500">
              {Math.round((step / 3) * 100)}% complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: Incident Type & Severity */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                What type of incident did you respond to?
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {incidentTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setFormData(prev => ({ ...prev, incidentType: type.value as IncidentType }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.incidentType === type.value
                        ? 'border-red-500 bg-red-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="text-2xl mb-2">{type.icon}</div>
                    <div className="text-sm font-medium text-gray-900">
                      {type.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                How severe was this incident? (1-10)
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Low</span>
                  <span className="text-sm text-gray-600">Critical</span>
                </div>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
                    <button
                      key={level}
                      onClick={() => setFormData(prev => ({ ...prev, severity: level as SeverityLevel }))}
                      className={`h-12 rounded-lg font-semibold text-white transition-all ${
                        formData.severity === level
                          ? `${getSeverityColor(level)} shadow-lg scale-110`
                          : `${getSeverityColor(level)} opacity-50 hover:opacity-75`
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                {formData.severity && (
                  <div className="text-center">
                    <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                      formData.severity <= 3 ? 'bg-green-100 text-green-800' :
                      formData.severity <= 6 ? 'bg-yellow-100 text-yellow-800' :
                      formData.severity <= 8 ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {getSeverityLabel(formData.severity)} Severity
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={handleNext}
                disabled={!formData.incidentType || !formData.severity}
                className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <span>Next</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Current State */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                How are you feeling right now?
              </h2>
              <textarea
                value={formData.currentState}
                onChange={(e) => setFormData(prev => ({ ...prev, currentState: e.target.value }))}
                placeholder="Describe how you're feeling physically and emotionally..."
                className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              />
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Physical Symptoms (select all that apply)
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {physicalSymptoms.map((symptom) => (
                  <button
                    key={symptom}
                    onClick={() => toggleSymptom(symptom)}
                    className={`p-3 rounded-lg border-2 text-sm transition-all ${
                      formData.physicalSymptoms.includes(symptom)
                        ? 'border-red-500 bg-red-50 text-red-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {symptom}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Emotional State (select all that apply)
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {emotionalStates.map((state) => (
                  <button
                    key={state}
                    onClick={() => toggleEmotionalState(state)}
                    className={`p-3 rounded-lg border-2 text-sm transition-all ${
                      formData.emotionalState.includes(state)
                        ? 'border-red-500 bg-red-50 text-red-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {state}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4 gap-2">
              <button
                onClick={handleBack}
                className="px-4 sm:px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleNext}
                disabled={!formData.currentState.trim()}
                className="px-4 sm:px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <span>Next</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Details & Needs */}
        {step === 3 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Share more about what happened (optional)
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                You can share as much or as little as you're comfortable with. This helps understand the context. Remember, this is private and confidential.
              </p>
              <textarea
                value={formData.whatHappened}
                onChange={(e) => setFormData(prev => ({ ...prev, whatHappened: e.target.value }))}
                placeholder="Describe the incident..."
                className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              />
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                What do you need right now? (optional)
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                What would be most helpful for you at this moment.
              </p>
              <textarea
                value={formData.immediateNeeds}
                onChange={(e) => setFormData(prev => ({ ...prev, immediateNeeds: e.target.value }))}
                placeholder="E.g., I need to talk to someone, I need resources, I need to decompress..."
                className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Heart className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">
                    Next Steps
                  </h3>
                  <p className="text-sm text-blue-800">
                    After you submit, we'll connect you with GRIPS (our peer support chatbot) to continue the conversation and get you the support you need.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4 gap-2">
              <button
                onClick={handleBack}
                className="px-4 sm:px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 sm:px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <Activity className="w-5 h-5 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Submit & Connect with GRIPS</span>
                    <span className="sm:hidden">Connect GRIPS</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};




